import { Component } from 'react'
import { RemoteOperation, Router, ShippingOperationConfig, showToastError } from 'stylewhere/shared'
import { T, __ } from 'stylewhere/i18n'
import { Box, Icons, Page, ParcelRightHeader, ParcelRowItemList } from 'stylewhere/components'
import { ParcelContentEntry, Parcels, ShippingParcel } from 'stylewhere/api'
import { hasChecklist } from 'stylewhere/shared/utils'

interface ShippingParcelDetailParams {
  opCode: string
  asnId: string
  parcelId: string
}

interface LocationState {
  parcel: ShippingParcel
  filterParams?: Record<string, any>
}

interface ShippingParcelDetailProps {
  match: { params: ShippingParcelDetailParams }
  location: { state?: LocationState }
}

interface ShippingParcelDetailState {
  parcel?: ShippingParcel
  entries?: ParcelContentEntry[]
  loading: boolean
}

export default class ShippingParcelDetail extends Component<ShippingParcelDetailProps, ShippingParcelDetailState> {
  parcelId: string
  opCode: string
  operation: ShippingOperationConfig

  get locationState(): LocationState | undefined {
    return this.props.location.state
  }

  state: ShippingParcelDetailState = {
    loading: true
  }

  constructor(props: any) {
    super(props)
    const matchParams: ShippingParcelDetailParams = this.props.match.params
    this.parcelId = matchParams.parcelId
    this.opCode = matchParams.opCode
    this.operation = RemoteOperation.getOperationConfig<ShippingOperationConfig>(this.opCode)
  }

  componentDidMount() {
    this.load()
  }

  load = async () => {
    try {
      const loadedParcel = await Parcels.get<ShippingParcel>(this.parcelId)
      if (!loadedParcel) throw Error('Parcel not found')
      const entries = await Parcels.parcelContent(
        loadedParcel.operationId ?? this.operation.id,
        loadedParcel.id,
        this.operation.readingsDisplayMode === 'item' ? 'item' : 'product'
      )
      this.setState({
        parcel: loadedParcel,
        entries: entries,
        loading: false
      })
    } catch (error) {
      showToastError(error, __(T.error.error))
      this.goBack()
    }
  }

  getParcelCounter = () => {
    const { parcel, entries } = this.state

    let entryDetectedItems = 0
    let entryUnexpectedItems = 0
    let entryExpectedItems = 0
    if (entries && entries.length > 0) {
      entries.map((e) => {
        entryDetectedItems += e.detected
        entryExpectedItems += e.expected
        entryUnexpectedItems += e.unexpected
      })
    }

    const parcelEntryQuantity =
      parcel.parcelEntryQuantity && parcel.parcelEntryQuantity !== null ? parcel.parcelEntryQuantity : 0
    const checklistExpectedQuantity = hasChecklist(this.operation)
      ? parcel.checklistExpectedQuantity && parcel.checklistExpectedQuantity !== null
        ? parcel.checklistExpectedQuantity
        : 0
      : undefined

    return {
      detected:
        entryDetectedItems > 0 && entryDetectedItems !== parcelEntryQuantity ? entryDetectedItems : parcelEntryQuantity,
      expected: checklistExpectedQuantity
        ? entryExpectedItems > 0 && entryExpectedItems !== checklistExpectedQuantity
          ? entryExpectedItems
          : checklistExpectedQuantity
        : undefined
    }
  }

  goBack = () => {
    const filterParams = this.locationState?.filterParams ?? {}
    const { parcel } = this.state
    const withEncoding = this.operation.templatePath === 'withEncoding'
    if (parcel && parcel.asn && this.operation.shippingMode === 'shipment') {
      Router.navigate('/shipping/:opCode/asn/:asnId', { opCode: this.opCode, asnId: parcel.asn.id, ...filterParams })
    } else {
      Router.navigate(withEncoding ? '/shipping/withEncoding/:opCode' : '/shipping/:opCode', {
        opCode: this.opCode,
        ...filterParams
      })
    }
  }

  render() {
    const { loading, parcel, entries } = this.state
    if (loading) {
      return (
        <Page
          title={__(T.misc.loading)}
          onBackPress={() => {
            this.goBack()
          }}
        >
          <Page.Content notBoxed>
            <Box flex center>
              <Icons.Loader1 />
            </Box>
          </Page.Content>
        </Page>
      )
    }

    const counter = this.getParcelCounter()
    return (
      <Page
        headerRight={
          <ParcelRightHeader
            isParcel
            shipmentCode={parcel.code}
            totalExpecteds={counter.expected}
            totalReadings={counter.detected}
            canDelete={false}
          />
        }
        title={this.operation.description}
        onBackPress={this.goBack}
      >
        <Box bg={'#FFF'} flex>
          <Page.Content notBoxed>
            <ParcelRowItemList entries={entries} parcel={parcel} operation={this.operation} viewHeader={true} />
          </Page.Content>
        </Box>
      </Page>
    )
  }
}
